import { supabase } from '../lib/supabase'
import type {
  MarketingLink,
  MarketingLinkInput,
  MarketingLinkKind,
  SoftDeleteParams,
} from '../types/marketingLinks'

const SELECT_LIST = `
  *,
  created_by_user:profiles!marketing_links_created_by_fkey(id, name)
`

function softDeletePayload(params?: SoftDeleteParams) {
  return {
    deleted_at: new Date().toISOString(),
    deleted_by: params?.deleted_by ?? null,
    deleted_reason: params?.deleted_reason ?? null,
  }
}

export const marketingLinksService = {
  async list(kind: MarketingLinkKind): Promise<MarketingLink[]> {
    const { data, error } = await supabase
      .from('marketing_links')
      .select(SELECT_LIST)
      .eq('kind', kind)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })

    if (error) throw error
    return (data || []) as MarketingLink[]
  },

  async create(
    kind: MarketingLinkKind,
    input: MarketingLinkInput,
    createdBy?: string | null
  ): Promise<MarketingLink> {
    const payload = {
      kind,
      title: input.title.trim(),
      description: input.description?.trim() || null,
      download_url: input.download_url.trim(),
      created_by: createdBy ?? null,
    }

    const { data, error } = await supabase
      .from('marketing_links')
      .insert(payload)
      .select(SELECT_LIST)
      .single()

    if (error) throw error
    return data as MarketingLink
  },

  async update(
    id: string,
    kind: MarketingLinkKind,
    input: MarketingLinkInput
  ): Promise<MarketingLink> {
    const payload = {
      title: input.title.trim(),
      description: input.description?.trim() || null,
      download_url: input.download_url.trim(),
    }

    const { data, error } = await supabase
      .from('marketing_links')
      .update(payload)
      .eq('id', id)
      .eq('kind', kind)
      .is('deleted_at', null)
      .select(SELECT_LIST)
      .single()

    if (error) throw error
    return data as MarketingLink
  },

  async softDelete(
    id: string,
    kind: MarketingLinkKind,
    params?: SoftDeleteParams
  ): Promise<void> {
    const { error } = await supabase
      .from('marketing_links')
      .update(softDeletePayload(params))
      .eq('id', id)
      .eq('kind', kind)
      .is('deleted_at', null)

    if (error) throw error
  },
}
